import {
    mount, unmount, setChildren
} from 'redom';
import remove from 'lodash/remove';
import find from 'lodash/find';

import { ScrollCatcher } from '../Catchers/scrollCatcher';
import { ElementEvent, ElementsManager } from '../Element/elementsManager';
import { minifyHTML, reorderArray } from '~/services/util';
import { ElementShape } from '~src/Element/elementShape';
import { Section, findParentHtmlByTag } from '~src/Section/section';
import { SystemCamera } from '~src/System/systemCamera';
import { Observable } from '~src/Tools/observable';
import { ScrollEvent } from '~src/Catchers/scrollPath';

const getVisibleHeightPx = (section: Section, viewportHeight: number) => {
    const rect = section.html.getBoundingClientRect();
    const height = rect.bottom - rect.top;
    const visible = {
        top: rect.top >= 0 && rect.top < viewportHeight,
        bottom: rect.bottom > 0 && rect.bottom < viewportHeight
    };
    let visiblePx = 0;

    if (visible.top && visible.bottom) {
        // Whole section is visible
        visiblePx = height;
    } else if (visible.top) {
        visiblePx = viewportHeight - rect.top;
    } else if (visible.bottom) {
        visiblePx = rect.bottom;
    } else if (height > viewportHeight && rect.top < 0) {
        const absTop = Math.abs(rect.top);

        if (absTop < height) {
            // Part of the element is visible
            visiblePx = height - absTop;
        }
    }

    return visiblePx;
};

export const SECTION_TAG = ['SECTION'];
// export const SECTION_TAG = ['SECTION', 'HEADER', 'FOOTER'];

export enum SectionEvent {
    Moved,
    Added,
    Removed,
    Rename,
}

export class SectionsManager extends Observable<SectionEvent, Section> {
    private htmlSections: HTMLElement[] = [];

    private sections: Section[] = [];

    private system: SystemCamera;

    private browser: HTMLElement;

    private scrollCatcher: ScrollCatcher;

    private elementsManager: ElementsManager;

    constructor(
        system: SystemCamera,
        browser: HTMLElement,
        scrollCatcher: ScrollCatcher,
        ElementsManager: ElementsManager
    ) {
        super('SECTION');
        this.system = system;
        this.browser = browser;
        this.scrollCatcher = scrollCatcher;
        this.elementsManager = ElementsManager;

        this.setPage(browser);
        this.setSystemEvent();
        this.setElementEvent();
    }

    private setSystemEvent() {
        this.scrollCatcher.on(ScrollEvent.Alignment, () => {
            this.checkAlignment();
        });
    }

    private setElementEvent() {
        this.elementsManager.on(ElementEvent.Added, (element) => {
            this.addElementSection(element);
        });

        this.elementsManager.on(ElementEvent.Removed, (element) => {
            this.removeElementSection(element);
        });

        this.elementsManager.on(ElementEvent.Loaded, () => {
            this.checkSectionsGeometry();
        });
    }

    private checkAlignment() {
        this.checkSectionsGeometry();
        setTimeout(() => {
            //! To make sure all alphaIndex are right depending on alignement
            this.elementsManager.checkGeometries();
        }, 100);
    }

    public checkSectionsGeometry() {
        this.sections.forEach((section) => {
            const perc = section.getTopPercentage();
            section.checkGeometry(perc);
        });
    }

    private addElementSection(element: ElementShape) {
        const section = this.getSectionFromElement(element);
        if (section) section.addElement(element);
        this.checkSectionsGeometry();
    }

    private removeElementSection(element: ElementShape) {
        const section = this.getSectionFromElement(element);
        if (section) section.removeElement(element);
        this.checkSectionsGeometry();
    }

    private getSectionFromElement(element: ElementShape) {
        const sectionHtml = findParentHtmlByTag(element.html, SECTION_TAG);
        const section = this.getSectionFromHtml(sectionHtml);
        return section;
    }

    public getSectionFromHtml(section: HTMLElement): Section {
        return find(this.sections, (s) => s.html === section);
    }

    public setPageFromString(sectionsString: string) {
        this.browser.innerHTML = sectionsString;
        const children = this.browser.childNodes;
        this.addSections(children);
    }

    private setPage(sectionsParent: HTMLElement) {
        const sections = sectionsParent.querySelectorAll('section');
        this.addSections(sections);
    }

    public getPageSectionsString(): string {
        const html = this.browser.innerHTML;
        return minifyHTML(html);
    }

    private addSections(sections: NodeListOf<HTMLElement>) {
        this.sections = [];
        sections.forEach((child) => {
            if (SECTION_TAG.includes(child.tagName)) {
                this.addSection(child);
            }
        });
        //! Can't do that as childNodes is not an array
        // this.sections = this.browser.childNodes;
        this.checkSectionsGeometry();
    }

    private addSection(sectionHtml: HTMLElement, force?: boolean): Section {
        if (!sectionHtml.parentNode || force) mount(this.browser, sectionHtml);
        this.htmlSections.push(sectionHtml);
        const section = new Section(this.system, sectionHtml, this.scrollCatcher);
        this.sections.push(section);
        this.scrollCatcher.checkHeight();
        this.checkSectionsGeometry();
        this.notify(SectionEvent.Added, section);
        return section;
    }

    public addSectionFromString(section: string): Section {
        const minifiedSection = minifyHTML(section);
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = minifiedSection;
        const newSection = tempDiv.childNodes[0];
        return this.addSection(newSection, true);
    }

    public removeSection(sectionHtml: HTMLElement) {
        remove(this.htmlSections, (s) => s === sectionHtml);
        unmount(this.browser, sectionHtml);
        const section = remove(this.sections, (s) => s.html === sectionHtml)[0];
        section.elements.forEach((element) => {
            this.elementsManager.removeElement(element);
        });
        this.scrollCatcher.checkHeight();
        this.checkSectionsGeometry();
        this.notify(SectionEvent.Removed, section);
    }

    public removeAllSections() {
        setChildren(this.browser, []);
        this.htmlSections = [];
        this.sections = [];
        this.elementsManager.removeAllElements();
    }

    public moveSection(section: number, side: 'up' | 'down') {
        if (side === 'down') {
            this.reorderSections(section, section + 1);
        } else if (side === 'up') {
            this.reorderSections(section, section - 1);
        }
    }

    public reorderSections(startIndex: number, endIndex: number): Section[] {
        this.sections = reorderArray(this.sections, startIndex, endIndex);
        this.htmlSections = reorderArray(this.htmlSections, startIndex, endIndex);
        setChildren(this.browser, this.htmlSections);
        this.checkSectionsGeometry();
        this.notify(SectionEvent.Moved, null);
        return this.sections;
    }

    public getSections(): Section[] {
        return this.sections;
    }

    public getSectionFocused(): Section {
        let sectionFocused: Section;
        const viewportHeight = window.innerHeight;
        let max = 0;

        this.sections.forEach((s) => {
            const visiblePx = getVisibleHeightPx(s, viewportHeight);
            if (visiblePx > max) {
                max = visiblePx;
                sectionFocused = s;
            }
        });

        return sectionFocused;
    }
}
